/* The access editor shared by Team, its bulk editor and an account's Access
   view: one row per person × account, a preset switch, the permission groups
   and the Advanced fold (ADR 0019). */

import { useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmFanAccess, CrmLevel, CrmPermission } from '../../../api/client';
import { Btn, Icon, Seg, Spin } from '../../ChatAssistant/compact/kit';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { InfoHoverHint } from '../../InfoHoverHint';
import { FanslyLogo, OnlyFansLogo } from '../../PlatformLogo';
import {
  GROUPS, GRANTABLE, MEDIA_DOWNLOAD, PRESETS, applyPreset, crmOfItem, grantedCrm, groupState, holdsCrm, levelOf, presetOf, setFans, setLevel,
  toggleGroup, toggleItem,
  type Access, type PresetId,
} from './permissionModel';
import { useAccessSummary } from './people';

/** Avatar for people and agencies (accounts use AccountAvatar): the picture when
 *  there is one, else the initial letter. */
export function Avatar({ text, size, round, src }: { text: string; size: number; round?: boolean; src?: string | null }) {
  return (
    <span style={{
      width: size, height: size, flex: 'none', borderRadius: round ? 99 : 6, overflow: 'hidden',
      background: src ? 'transparent' : C.purple, color: '#fff', boxShadow: src ? `0 0 0 1px ${C.border}` : undefined,
      font: f(600, size >= 36 ? 15 : size >= 28 ? 12 : 11), display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : text}</span>
  );
}

export function Check({ state, disabled }: { state: 'all' | 'some' | 'none'; disabled?: boolean }) {
  const on = state !== 'none';
  return (
    <span style={{ width: 14, height: 14, flex: 'none', boxSizing: 'border-box', borderRadius: 4, border: `1px solid ${on ? C.purple : C.faint}`, background: on ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', opacity: disabled ? 0.35 : 1 }}>
      {on && <Icon name={state === 'all' ? 'check' : 'minus'} size={10} />}
    </span>
  );
}

const PLATFORMS = [
  { id: 'onlyfans', name: 'OnlyFans', Logo: OnlyFansLogo },
  { id: 'fansly', name: 'Fansly', Logo: FanslyLogo },
] as const;

/** OnlyFans and Fansly logos by a CRM permission: hovering one shows which of that
    platform's pages the permission opens and what it allows there. */
export function CrmPlatformHints({ permission }: { permission: CrmPermission }) {
  const { t } = useTranslation('agency');
  const label = t(`fleet.crm.${permission}`);
  return (
    <span className="tm-crm-platforms">
      {PLATFORMS.map(({ id, name, Logo }) => (
        <InfoHoverHint key={id} label={t('fleet.crmOn.label', { permission: label, platform: name })}
          trigger={<Logo size={13} title="" />} triggerClassName="tm-crm-logo">
          <div className="tm-crm-tip-head"><Logo size={14} title="" />{name} · {label}</div>
          <div className="tm-crm-tip-pages">{t('fleet.crmOn.pages')} {t(`fleet.crmOn.${id}.${permission}.pages`)}</div>
          <div>{t(`fleet.crmOn.${id}.${permission}.does`)}</div>
        </InfoHoverHint>
      ))}
    </span>
  );
}

/** A checkbox hit area of its own, so ticking never opens what the row opens. */
export function PickBox({ on, onToggle, disabled, title }: { on: boolean; onToggle: () => void; disabled?: boolean; title?: string }) {
  return (
    <button type="button" className="tm-pick" title={title} disabled={disabled} aria-pressed={on}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onToggle(); }}>
      <Check state={on ? 'all' : 'none'} disabled={disabled} />
    </button>
  );
}

/** Full / Chatter / None, or Custom when the grants match no preset. */
export function PresetSwitch({ access, onPick }: { access: Access; onPick: (id: PresetId) => void }) {
  const { t } = useTranslation('agency');
  const preset = presetOf(access);
  return (
    <>
      {!preset && <span className="tm-custom"><Icon name="sliders" size={11} />{t('fleet.preset.custom')}</span>}
      <Seg<PresetId | 'custom'>
        value={preset ?? 'custom'}
        onChange={(id) => { if (id !== 'custom') onPick(id); }}
        options={PRESETS.map((p) => ({ id: p.id, label: t(p.labelKey), title: t(p.tipKey) }))}
      />
    </>
  );
}

/** The permission groups as checkboxes, plus the Advanced fold. `fans` off hides
    Per-subscriber access (bulk edits never copy subscriber entries). */
export function AccessEditor({ access, onChange, fans = true }: { access: Access; onChange: (next: Access) => void; fans?: boolean }) {
  const { t } = useTranslation('agency');
  const [advanced, setAdvanced] = useState(false);
  const advancedCount = access.view.size + (fans ? access.fans.length : 0);
  return (
    <>
      <div className="tm-groups">
        {GROUPS.map((g) => {
          // Media download is only grantable alongside a CRM permission.
          const off = g.items.every((i) => i.keys.includes(MEDIA_DOWNLOAD)) && !holdsCrm(access.has);
          return (
          <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <button type="button" disabled={off} title={off ? t('ts.needsCrm') : undefined} onClick={() => onChange(toggleGroup(access, g))} className="tm-check" style={{ font: f(600, 12), color: C.text, opacity: off ? 0.55 : undefined }}>
              <Check state={groupState(access, g)} />{t(g.labelKey)}
            </button>
            {g.items.map((i) => {
              const crm = crmOfItem(i.id);
              const check = (
                <button key={i.id} type="button" disabled={off} title={off ? t('ts.needsCrm') : undefined} onClick={() => onChange(toggleItem(access, i, g))} className="tm-check" style={{ paddingLeft: 12, font: f(400, 12.5), color: C.body, minHeight: 24, opacity: off ? 0.55 : undefined }}>
                  <Check state={i.keys.every((k) => access.has.has(k)) ? 'all' : 'none'} />{t(i.labelKey)}
                </button>
              );
              return crm ? <div key={i.id} className="tm-crm-row">{check}<CrmPlatformHints permission={crm} /></div> : check;
            })}
          </div>
          );
        })}
      </div>
      <div className="tm-advanced">
        <button type="button" onClick={() => setAdvanced(!advanced)} className="tm-advanced-toggle">
          <Icon name="chevron-right" size={13} color={C.muted} style={{ transform: advanced ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
          <span style={{ font: f(600, 12), color: C.text }}>{t('fleet.advanced')}</span>
          <span style={{ font: f(400, 11.5), color: C.muted }}>{advancedCount ? t('fleet.advancedCount', { count: advancedCount }) : fans ? t('fleet.advancedHint') : t('fleet.advancedHintBulk')}</span>
        </button>
        {advanced && <AdvancedAccess access={access} onChange={onChange} fans={fans} />}
      </div>
    </>
  );
}

export function AccessRow({ avatar, title, sub, locked, readOnly, access, saving, dirty, onChange, pick, extra, defaultOpen = false }: {
  avatar: ReactNode;
  title: ReactNode;
  /** Replaces the grants summary under the title. */
  sub?: ReactNode;
  /** The person owns the account: nothing to grant. */
  locked?: boolean;
  /** Summary only — no preset switch, no fold (bulk mode lists accounts this way). */
  readOnly?: boolean;
  access: Access;
  saving?: boolean;
  /** Edited but not saved yet (Team keeps a draft until Save). */
  dirty?: boolean;
  onChange: (next: Access) => void;
  pick?: { on: boolean; onToggle: () => void; disabled?: boolean; title?: string };
  extra?: ReactNode;
  /** Start with the editor open (a person just added). */
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation('agency');
  const summary = useAccessSummary();
  const [open, setOpen] = useState(defaultOpen);
  const editable = !locked && !readOnly;
  // Dropping Chats drops its subscriber entries — say so before they go.
  const change = (next: Access) => {
    if (access.fans.length && !next.fans.length && !confirm(t('fleet.confirmDropFans', { count: access.fans.length }))) return;
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.divider}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', padding: '8px 14px', minHeight: 44 }}>
        <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          {pick && <PickBox {...pick} />}
          <button type="button" onClick={() => editable ? setOpen(!open) : pick && !pick.disabled && pick.onToggle()}
            style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: 0, border: 0, background: 'transparent', cursor: editable || (pick && !pick.disabled) ? 'pointer' : 'default', textAlign: 'left' }}>
            {!readOnly && <Icon name="chevron-right" size={14} color={editable ? C.muted : 'transparent'} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />}
            {avatar}
            <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span className="tm-ellipsis" style={{ font: f(500, 13), color: C.text }}>{title}</span>
              <span className="tm-ellipsis" style={{ font: f(400, 11.5), color: C.muted }}>{locked ? t('fleet.ownsThis') : sub ?? summary(access)}</span>
            </span>
          </button>
        </div>
        {locked ? (
          <span className="tm-owner-pill"><Icon name="lock" size={11} />{t('fleet.ownerLocked')}</span>
        ) : !readOnly && (
          <>
            {saving && <Spin size={11} />}
            {dirty && <span className="tm-unsaved">{t('team.unsaved')}</span>}
            <PresetSwitch access={access} onPick={(id) => change(applyPreset(id))} />
            <span style={{ width: 40, flex: 'none', textAlign: 'right', font: f(500, 12), color: access.has.size ? C.purplePale : C.muted }}>{access.has.size}/{GRANTABLE.length}</span>
          </>
        )}
        {extra}
      </div>
      {open && editable && <AccessEditor access={access} onChange={change} />}
    </div>
  );
}

/** The Advanced fold: CRM Permission levels and Per-subscriber access. */
function AdvancedAccess({ access, onChange, fans: showFans }: { access: Access; onChange: (next: Access) => void; fans: boolean }) {
  const { t } = useTranslation('agency');
  const crm = grantedCrm(access);
  const levelOpts = [
    { id: 'full' as CrmLevel, label: t('fleet.level.full'), title: t('fleet.level.fullTip') },
    { id: 'view' as CrmLevel, label: t('fleet.level.view'), title: t('fleet.level.viewTip') },
  ];

  return (
    <div className="tm-advanced-body">
      <section className="tm-adv-section">
        <span style={{ font: f(600, 12), color: C.text }}>{t('fleet.levelsTitle')}</span>
        <span style={{ font: f(400, 11.5), color: C.sub }}>{t('fleet.levelsHint')}</span>
        {!crm.length && <span style={{ font: f(400, 12), color: C.muted }}>{t('fleet.levelsEmpty')}</span>}
        <div className="tm-levels">
          {crm.map((p: CrmPermission) => (
            <div key={p} className="tm-level-row">
              <span style={{ flex: 1, minWidth: 0, font: f(400, 12.5), color: C.body }}>{t(`fleet.crm.${p}`)}</span>
              <Seg<CrmLevel> value={levelOf(access, p)} onChange={(l) => onChange(setLevel(access, p, l))} options={levelOpts} />
            </div>
          ))}
        </div>
      </section>
      {showFans && (
        <section className="tm-adv-section">
          <span style={{ font: f(600, 12), color: C.text }}>{t('fleet.fansTitle')}</span>
          <span style={{ font: f(400, 11.5), color: C.sub }}>{t('fleet.fansHint')}</span>
          <FansEditor access={access} onChange={onChange} />
        </section>
      )}
    </div>
  );
}

/** Per-subscriber access on one account: entries that hide a fan or make them
    View only. Needs Chats; an entry never offers more than Chats has. */
export function FansEditor({ access, onChange }: { access: Access; onChange: (next: Access) => void }) {
  const { t } = useTranslation('agency');
  const hasChats = grantedCrm(access).includes('chats');
  const fanOpts = [
    ...(levelOf(access, 'chats') === 'full' ? [{ id: 'full' as CrmFanAccess['level'], label: t('fleet.level.full') }] : []),
    { id: 'view' as CrmFanAccess['level'], label: t('fleet.level.view') },
    { id: 'none' as CrmFanAccess['level'], label: t('fleet.level.none') },
  ];
  const [draft, setDraft] = useState('');
  const addFan = () => {
    const id = draft.trim();
    if (!id) return;
    setDraft('');
    if (access.fans.some((x) => x.fan_id === id)) return;
    onChange(setFans(access, [...access.fans, { fan_id: id, level: 'none' }]));
  };
  const setFanLevel = (fanId: string, level: CrmFanAccess['level']) =>
    onChange(setFans(access, access.fans.map((x) => (x.fan_id === fanId ? { ...x, level } : x))));
  const removeFan = (fanId: string) => onChange(setFans(access, access.fans.filter((x) => x.fan_id !== fanId)));
  if (!hasChats) return <span style={{ font: f(400, 12), color: C.muted }}>{t('fleet.fansNeedChats')}</span>;
  return (
    <>
      {access.fans.map((x) => (
        <div key={x.fan_id} className="tm-level-row">
          <Icon name="user" size={12} color={C.muted} />
          <span className="tm-ellipsis" style={{ flex: 1, minWidth: 0, font: f(500, 12.5), color: C.text }}>{x.fan_id}</span>
          <Seg<CrmFanAccess['level']> value={x.level} onChange={(l) => setFanLevel(x.fan_id, l)} options={fanOpts} />
          <button type="button" title={t('fleet.removeFan')} onClick={() => removeFan(x.fan_id)} className="cpt-hover-icon tm-x"><Icon name="x" size={12} /></button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, maxWidth: 360 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addFan(); }}
          placeholder={t('fleet.fanPlaceholder')} className="tm-input" style={{ flex: 1, height: 28 }} />
        <Btn kind="ghost" icon="plus" onClick={addFan} disabled={!draft.trim()}>{t('fleet.addFan')}</Btn>
      </div>
    </>
  );
}

/** A search box in the Team look. */
export function SearchInput({ value, onChange, placeholder, style }: { value: string; onChange: (v: string) => void; placeholder: string; style?: CSSProperties }) {
  return (
    <div className="tm-search" style={style}>
      <Icon name="search" size={13} color={C.muted} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        onKeyDown={(e) => { if (e.key === 'Escape') onChange(''); }} />
      {value && (
        <button type="button" onClick={() => onChange('')} className="cpt-hover-icon tm-x" style={{ width: 20, height: 20 }} aria-label="Clear">
          <Icon name="x" size={11} />
        </button>
      )}
    </div>
  );
}
